import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2, Check, Phone, Lock } from 'lucide-react';
import { authApi } from '../utils/api';

const AccountSettings = () => {
  const navigate = useNavigate();

  // Profile info
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);

  // Password change
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  // Phone update
  const [newPhone, setNewPhone] = useState('');
  const [phoneCode, setPhoneCode] = useState('');
  const [phoneStep, setPhoneStep] = useState('input'); // 'input' or 'verify'
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [phoneSuccess, setPhoneSuccess] = useState('');

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const data = await authApi.getMe();
      setProfile(data);
    } catch (err) {
      console.error('Failed to load profile:', err);
    } finally {
      setLoadingProfile(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    setPasswordLoading(true);
    try {
      const hasPassword = profile?.has_password;
      // No current password to send when setting an initial one.
      const result = await authApi.changePassword(hasPassword ? currentPassword : '', newPassword);
      if (result.detail) {
        setPasswordError(result.detail);
      } else {
        setPasswordSuccess(hasPassword
          ? 'Password changed successfully'
          : 'Password set — you can now sign in with your email and password.');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        loadProfile();   // refresh has_password so the form switches to Change mode
      }
    } catch (err) {
      setPasswordError('An error occurred. Please try again.');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handlePhoneRequest = async (e) => {
    e.preventDefault();
    setPhoneError('');
    setPhoneSuccess('');
    setPhoneLoading(true);

    try {
      const result = await authApi.updatePhone(newPhone);
      if (result.detail) {
        setPhoneError(result.detail);
      } else {
        setPhoneStep('verify');
      }
    } catch (err) {
      setPhoneError('An error occurred. Please try again.');
    } finally {
      setPhoneLoading(false);
    }
  };

  const handlePhoneVerify = async (e) => {
    e.preventDefault();
    setPhoneError('');
    setPhoneLoading(true);

    try {
      const result = await authApi.verifyPhoneUpdate(phoneCode);
      if (result.detail) {
        setPhoneError(result.detail);
      } else {
        setPhoneSuccess('Phone number updated successfully');
        setPhoneStep('input');
        setNewPhone('');
        setPhoneCode('');
        loadProfile(); // Refresh profile
      }
    } catch (err) {
      setPhoneError('An error occurred. Please try again.');
    } finally {
      setPhoneLoading(false);
    }
  };

  const formatPhone = (phone) => {
    if (!phone) return 'Not set';
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) {
      return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return phone;
  };

  if (loadingProfile) {
    return (
      <div className="dashboard-loading">
        <Loader2 className="spinner-large" size={48} />
        <p>Loading account settings...</p>
      </div>
    );
  }

  return (
    <div className="dashboard">

      <main className="dashboard-content">
        <div className="welcome-section">
          <h2>Account Settings</h2>
          <p>Manage your password and phone number.</p>
        </div>

        <div className="settings-grid">
          {/* Account Info */}
          <div className="settings-card">
            <div className="settings-card-header">
              <h3>Account Information</h3>
            </div>
            <div className="settings-card-body">
              <div className="info-row">
                <span className="info-label">Email</span>
                <span className="info-value">{profile?.email}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Name</span>
                <span className="info-value">{profile?.first_name} {profile?.last_name}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Company</span>
                <span className="info-value">{profile?.customer_name}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Phone</span>
                <span className="info-value">{formatPhone(profile?.phone)}</span>
              </div>
            </div>
          </div>

          {/* Set / Change Password */}
          <div className="settings-card">
            <div className="settings-card-header">
              <Lock size={18} />
              <h3>{profile?.has_password ? 'Change Password' : 'Set a Password'}</h3>
            </div>
            <div className="settings-card-body">
              {!profile?.has_password && (
                <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 12px' }}>
                  Set a password to sign in with your email and password instead of the
                  one‑time login link.
                </p>
              )}
              {passwordError && <div className="error-message">{passwordError}</div>}
              {passwordSuccess && <div className="success-message">{passwordSuccess}</div>}

              <form onSubmit={handlePasswordChange}>
                {profile?.has_password && (
                  <div className="form-group">
                    <label>Current Password</label>
                    <div className="password-input">
                      <input
                        type={showCurrent ? 'text' : 'password'}
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        required
                        autoComplete="current-password"
                      />
                      <button type="button" className="password-toggle" onClick={() => setShowCurrent(!showCurrent)}>
                        {showCurrent ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label>New Password</label>
                  <div className="password-input">
                    <input
                      type={showNew ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={8}
                      placeholder="At least 8 characters"
                      autoComplete="new-password"
                    />
                    <button type="button" className="password-toggle" onClick={() => setShowNew(!showNew)}>
                      {showNew ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label>Confirm New Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                  />
                </div>

                <button type="submit" className="submit-button" disabled={passwordLoading}>
                  {passwordLoading
                    ? <Loader2 className="spinner" size={18} />
                    : <><Check size={18} /> {profile?.has_password ? 'Update Password' : 'Set Password'}</>}
                </button>
              </form>
            </div>
          </div>

          {/* Update Phone */}
          <div className="settings-card">
            <div className="settings-card-header">
              <Phone size={18} />
              <h3>Update Phone Number</h3>
            </div>
            <div className="settings-card-body">
              <p className="settings-note">
                Your phone number is used for two-factor authentication.
                Current: <strong>{formatPhone(profile?.phone)}</strong>
              </p>

              {phoneError && <div className="error-message">{phoneError}</div>}
              {phoneSuccess && <div className="success-message">{phoneSuccess}</div>}

              {phoneStep === 'input' ? (
                <form onSubmit={handlePhoneRequest}>
                  <div className="form-group">
                    <label>New Phone Number</label>
                    <input
                      type="tel"
                      value={newPhone}
                      onChange={(e) => setNewPhone(e.target.value)}
                      placeholder="(555) 123-4567"
                      required
                    />
                  </div>
                  <button type="submit" className="submit-button" disabled={phoneLoading}>
                    {phoneLoading ? <Loader2 className="spinner" size={18} /> : 'Send Verification Code'}
                  </button>
                </form>
              ) : (
                <form onSubmit={handlePhoneVerify}>
                  <p className="code-info">
                    A verification code has been sent to your new phone number.
                  </p>
                  <div className="form-group">
                    <label>Verification Code</label>
                    <input
                      type="text"
                      value={phoneCode}
                      onChange={(e) => setPhoneCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      required
                      maxLength={6}
                      className="code-input"
                    />
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="submit" className="submit-button" disabled={phoneLoading}>
                      {phoneLoading ? <Loader2 className="spinner" size={18} /> : 'Verify & Update'}
                    </button>
                    <button
                      type="button"
                      className="resend-button"
                      onClick={() => { setPhoneStep('input'); setPhoneCode(''); setPhoneError(''); }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AccountSettings;
